
import { useState, useEffect } from 'react';
import { Users, Store, ShoppingBag, TrendingUp, DollarSign } from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { BASE_URL } from '../Api/Config';


export function Card({ children, className }) {
  return (
    <div className={`bg-white rounded-2xl shadow ${className || ""}`}>
      {children}
    </div>
  );
}

export function CardContent({ children, className }) {
  return (
    <div className={`p-4 ${className || ""}`}>
      {children}
    </div>
  );
}

export default function Dashboard() {

  const [stats, setStats] = useState(null); // 👈 add this
  const [loading, setLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem("token");

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch(`${BASE_URL}/admin/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || {}); // 👈 now this works
      } else {
        console.error("Failed to fetch stats");
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line
  }, []);


  const orderData = [
    { month: "Jan", orders: 320 },
    { month: "Feb", orders: 280 },
    { month: "Mar", orders: 450 },
    { month: "Apr", orders: 390 },
    { month: "May", orders: 510 },
    { month: "Jun", orders: 610 },
  ];

  const revenueData = [
    { month: "Jan", revenue: 5200 },
    { month: "Feb", revenue: 4800 },
    { month: "Mar", revenue: 7200 },
    { month: "Apr", revenue: 6100 },
    { month: "May", revenue: 8900 },
    { month: "Jun", revenue: 10000 },
  ];

  const restaurantDistribution = [
    { name: "Italian", value: 20 },
    { name: "Chinese", value: 15 },
    { name: "Indian", value: 25 },
    { name: "Fast Food", value: 25 },
    { name: "Other", value: 20 },
  ];

  const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#3b82f6"];

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Admin Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card>
          <CardContent className="flex items-center p-6">
            <Users className="w-10 h-10 text-blue-500" />
            <div className="ml-4">
              <h2 className="text-sm font-semibold">Users</h2>
              <p className="text-xl font-bold">{stats?.totalUsers || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <Store className="w-10 h-10 text-green-500" />
            <div className="ml-4">
              <h2 className="text-sm font-semibold">Restaurants</h2>
              <p className="text-xl font-bold">{stats?.totalRestaurants || 0}</p>
              <p className="text-xs text-gray-500">
                Active: {stats?.activeRestaurants || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md rounded-2xl col-span-1">
          <CardContent className="flex items-center p-6">
            <ShoppingBag className="w-10 h-10 text-purple-500" />
            <div className="ml-4">
              <h2 className="text-sm font-semibold">Monthly Orders</h2>
              <p className="text-xl font-bold">{stats?.monthlyOrders || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md rounded-2xl col-span-1">
          <CardContent className="flex items-center p-6">
            <DollarSign className="w-10 h-10 text-yellow-500" />
            <div className="ml-4">
              <h2 className="text-sm font-semibold">Revenue</h2>
              <p className="text-xl font-bold">{stats?.revenue || "$0"}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md rounded-2xl col-span-1">
          <CardContent className="flex items-center p-6">
            <TrendingUp className="w-10 h-10 text-red-500" />
            <div className="ml-4">
              <h2 className="text-sm font-semibold">Growth</h2>
              <p className="text-xl font-bold">{stats?.growth || "0%"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders & Revenue Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-md rounded-2xl">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Orders Overview</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={orderData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="orders" fill="#6366f1" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="shadow-md rounded-2xl">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Revenue Trend</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} dot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Restaurant Distribution */}
      <Card className="shadow-md rounded-2xl">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold mb-4">Restaurant Categories</h2>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={restaurantDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius="80%"
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {restaurantDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}